/**
 * shell/ResearchCalibrationPage.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Pilot-wide validation statistics — Sensitivity/Specificity/ROC/Youden's J,
 * Cronbach's α, Cohen's κ. Genuinely a different kind of page from the rest
 * of the Research Dashboard: there's no per-child line here, no days-since-
 * induction axis. Every number on this page is a property of the SCREENING
 * TOOL across the whole sample, not any individual child's trajectory.
 *
 * Routed at /research/calibration.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { PANCHAKOSHA } from "@/constants/ncfFramework";
import { API, selectStyle } from "./ResearchShared";

function fetchCalibration(getAuthHeader, metric, source, kosha) {
  const params = new URLSearchParams({ source, kosha });
  return fetch(`${API}/research/calibration/${metric}?${params}`, { headers: getAuthHeader() })
    .then(r => r.json());
}

// ── ROC curve — sensitivity vs (1 - specificity), with the diagonal
// "no better than chance" reference and the Youden's J-optimal point
// highlighted. A genuinely different chart shape from MultiLineChart's
// time-series, so it isn't shared from ResearchShared.
function RocCurve({ curve, best, width = 360, height = 360 }) {
  if (!curve?.length) return null;
  const pad = 36;
  const inner = width - pad * 2;
  const sx = x => pad + x * inner;
  const sy = y => pad + inner - y * inner;
  const points = [...curve].sort((a, b) => (1 - a.specificity) - (1 - b.specificity));
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(1 - p.specificity)} ${sy(p.sensitivity)}`).join(' ');
  const bestX = sx(1 - best.specificity), bestY = sy(best.sensitivity);

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <line x1={pad} y1={pad} x2={pad} y2={height - pad} stroke="#E5E7EB" />
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#E5E7EB" />
      <line x1={sx(0)} y1={sy(0)} x2={sx(1)} y2={sy(1)} stroke="#E5E7EB" strokeDasharray="4 4" />
      <path d={d} fill="none" stroke="#9333EA" strokeWidth={2} strokeLinejoin="round" />
      <circle cx={bestX} cy={bestY} r={5} fill="#EA580C" stroke="#fff" strokeWidth={1.5} />
      <text x={pad} y={height - pad + 16} fontSize={9} fill="#9CA3AF">0</text>
      <text x={width - pad} y={height - pad + 16} fontSize={9} fill="#9CA3AF" textAnchor="end">1 − Specificity</text>
      <text x={pad - 6} y={height - pad} fontSize={9} fill="#9CA3AF" textAnchor="end">0</text>
      <text x={pad - 6} y={pad + 4} fontSize={9} fill="#9CA3AF" textAnchor="end">1</text>
      <text x={pad} y={pad - 8} fontSize={9} fill="#9CA3AF">Sensitivity</text>
    </svg>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 14px', flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: '#6B7280', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '18px 20px', marginBottom: 18 }}>
      <div style={{ fontWeight: 800, fontSize: 14, color: '#111827', marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function pct(v) { return v == null ? '—' : `${Math.round(v * 1000) / 10}%`; }
function ci(range) { return (!range || range[0] == null) ? '' : `(95% CI ${pct(range[0])}–${pct(range[1])})`; }

function alphaInterpretation(a) {
  if (a == null) return '';
  if (a >= 0.9) return 'excellent internal consistency';
  if (a >= 0.8) return 'good internal consistency';
  if (a >= 0.7) return 'acceptable internal consistency';
  if (a >= 0.6) return 'questionable — items may not be measuring the same thing consistently';
  return 'poor internal consistency';
}
function kappaInterpretation(k) {
  if (k == null) return '';
  if (k >= 0.81) return 'almost perfect agreement';
  if (k >= 0.61) return 'substantial agreement';
  if (k >= 0.41) return 'moderate agreement';
  if (k >= 0.21) return 'fair agreement';
  if (k >= 0)    return 'slight agreement';
  return 'agreement no better than chance';
}

export default function ResearchCalibrationPage() {
  const { getAuthHeader } = useAuth();
  const [source, setSource] = useState('quickscreener');
  const [kosha,  setKosha]  = useState('overall');
  const [roc,     setRoc]     = useState(null);
  const [alpha,   setAlpha]   = useState(null);
  const [kappa,   setKappa]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchCalibration(getAuthHeader, 'roc-youden', source, kosha),
      kosha !== 'overall' ? fetchCalibration(getAuthHeader, 'cronbach-alpha', source, kosha) : Promise.resolve({ success: true, insufficientData: true, reason: 'Pick a specific domain — α is computed per domain, not overall.' }),
      fetchCalibration(getAuthHeader, 'cohens-kappa', source, kosha),
    ]).then(([r, a, k]) => {
      if (r.success) setRoc(r);
      if (a.success) setAlpha(a);
      if (k.success) setKappa(k);
    }).finally(() => setLoading(false));
  }, [source, kosha]);

  return (
    <div style={{ padding: '28px 26px 80px', maxWidth: 1100, margin: '0 auto', fontFamily: "'Inter',system-ui,sans-serif" }}>
      <div style={{ marginBottom: 18 }}>
        <Link to="/research" style={{ fontSize: 12, color: 'var(--mist)', textDecoration: 'none' }}>← Across-Group Dashboard</Link>
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 900, color: 'var(--navy)', lineHeight: 1 }}>Calibration</div>
        <div style={{ fontSize: 13, color: 'var(--mist)', marginTop: 5 }}>How well the screening tool itself performs against an independent clinical judgment — pilot-wide, not per child.</div>
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 20 }}>
        These numbers depend on staff having actually submitted clinical ratings. Empty panels just mean there isn't enough rated data yet for that combination, not an error.
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <select value={source} onChange={e => setSource(e.target.value)} style={selectStyle}>
          <option value="quickscreener">QuickScreener rounds</option>
          <option value="srt">Full SRT sessions</option>
        </select>
        <select value={kosha} onChange={e => setKosha(e.target.value)} style={selectStyle}>
          <option value="overall">Overall</option>
          {PANCHAKOSHA.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
        </select>
      </div>

      <Panel title="Sensitivity, Specificity, ROC & Youden's J">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>
        ) : !roc || roc.insufficientData ? (
          <div style={{ fontSize: 12.5, color: '#9CA3AF' }}>{roc?.reason || `Not enough clinical ratings yet for this combination (have ${roc?.n ?? 0}, need at least 4 with both concern and no-concern represented).`}</div>
        ) : (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ width: 320, flexShrink: 0 }}>
              <RocCurve curve={roc.curve} best={roc.best} />
            </div>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 12, color: '#374151', marginBottom: 10 }}>
                Best cutoff (max Youden's J): <strong>{roc.best.cutoff}</strong> — J = {Math.round(roc.best.youdenJ * 1000) / 1000}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <StatCard label="Sensitivity" value={pct(roc.best.sensitivity)} sub={ci(roc.sensitivityCI)} />
                <StatCard label="Specificity" value={pct(roc.best.specificity)} sub={ci(roc.specificityCI)} />
                <StatCard label="AUC" value={Math.round(roc.auc * 1000) / 1000} />
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                n = {roc.n} rated instances ({roc.positives} concern, {roc.negatives} no-concern, per independent clinical rating)
              </div>
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Cronbach's α — internal consistency">
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>
        ) : !alpha || alpha.insufficientData ? (
          <div style={{ fontSize: 12.5, color: '#9CA3AF' }}>{alpha?.reason || `Not enough completed ${source === 'srt' ? 'SRT sessions' : 'QuickScreener rounds'} yet for this domain (have ${alpha?.n ?? 0}, need at least 3).`}</div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <StatCard label="α" value={Math.round(alpha.alpha * 1000) / 1000} sub={alphaInterpretation(alpha.alpha)} />
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>
              n = {alpha.n} children (one administration each, the earliest), {alpha.items} items
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Cohen's κ — inter-rater agreement">
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>
        ) : !kappa || kappa.insufficientData ? (
          <div style={{ fontSize: 12.5, color: '#9CA3AF' }}>
            {`Not enough double-rated instances yet (have ${kappa?.n ?? 0} rater pairs, need at least 5). This needs two different staff members each submitting an independent verdict for the same screening instance on the same day.`}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <StatCard label="κ" value={Math.round(kappa.kappa * 1000) / 1000} sub={kappaInterpretation(kappa.kappa)} />
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>n = {kappa.n} rater pairs</div>
          </div>
        )}
      </Panel>
    </div>
  );
}